// Small in-house Markdown parser for the User Guide content subset
// (see CLAUDE.md "User guide" section / guide-style.md):
//   # ## ### headings, paragraphs, "-" / "1." lists (one+ nesting levels),
//   **bold**, *italic*, `code`, GFM tables, blockquotes (callouts when they
//   start with a bold "label:"), links, images, horizontal rules, fenced code.
//
// It produces a plain AST that MarkdownView renders as React elements — there
// is NO innerHTML anywhere, so a stray "<" in prose is just text. Link hrefs
// are restricted to safe schemes (see isSafeHref).

export type Inline =
  | { t: 'text'; text: string }
  | { t: 'bold'; children: Inline[] }
  | { t: 'italic'; children: Inline[] }
  | { t: 'code'; text: string }
  | { t: 'link'; href: string; children: Inline[] }
  | { t: 'image'; src: string; alt: string };

export type CalloutKind = 'tip' | 'note' | 'important';

export type Block =
  | { t: 'heading'; level: 1 | 2 | 3; children: Inline[]; text: string }
  | { t: 'paragraph'; children: Inline[] }
  | { t: 'list'; ordered: boolean; items: Block[][] }
  | { t: 'table'; head: Inline[][]; rows: Inline[][][]; align: Array<'left' | 'right' | 'center' | null> }
  | { t: 'quote'; children: Block[] }
  | { t: 'callout'; kind: CalloutKind; label: string; children: Block[] }
  | { t: 'code'; text: string }
  | { t: 'hr' };

export interface ParsedDoc {
  blocks: Block[];
  /** Text of the first `#` heading, if any. */
  title: string | null;
  /** `##` headings, in order, with the index used as their anchor id. */
  sections: Array<{ index: number; text: string }>;
}

const CALLOUT_LABELS: Record<string, CalloutKind> = {
  'טיפ': 'tip',
  'שימו לב': 'note',
  'חשוב': 'important',
};

export function isSafeHref(href: string): boolean {
  return /^(\/|#|https?:\/\/|mailto:)/i.test(href.trim());
}

const SPECIAL = /[\\`*![]/;

export function parseInline(src: string): Inline[] {
  const out: Inline[] = [];
  let buf = '';
  const flush = () => {
    if (buf) {
      out.push({ t: 'text', text: buf });
      buf = '';
    }
  };
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (!SPECIAL.test(ch)) {
      buf += ch;
      i++;
      continue;
    }
    if (ch === '\\' && i + 1 < src.length && /[\\`*_{}[\]()#+\-.!|>~]/.test(src[i + 1])) {
      buf += src[i + 1];
      i += 2;
      continue;
    }
    if (ch === '`') {
      const end = src.indexOf('`', i + 1);
      if (end > i + 1) {
        flush();
        out.push({ t: 'code', text: src.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    if (ch === '*') {
      if (src.startsWith('**', i)) {
        const end = findClose(src, '**', i + 2);
        if (end > i + 2) {
          flush();
          out.push({ t: 'bold', children: parseInline(src.slice(i + 2, end)) });
          i = end + 2;
          continue;
        }
      } else if (i + 1 < src.length && !/\s/.test(src[i + 1])) {
        const end = findClose(src, '*', i + 1);
        if (end > i + 1 && !/\s/.test(src[end - 1])) {
          flush();
          out.push({ t: 'italic', children: parseInline(src.slice(i + 1, end)) });
          i = end + 1;
          continue;
        }
      }
    }
    if (ch === '!' && src[i + 1] === '[') {
      const m = matchLink(src, i + 1);
      if (m) {
        flush();
        out.push({ t: 'image', alt: m.label, src: m.href });
        i = m.end;
        continue;
      }
    }
    if (ch === '[') {
      const m = matchLink(src, i);
      if (m && isSafeHref(m.href)) {
        flush();
        out.push({ t: 'link', href: m.href, children: parseInline(m.label) });
        i = m.end;
        continue;
      }
    }
    buf += ch;
    i++;
  }
  flush();
  return out;
}

// Find the closing delimiter, skipping code spans and (for single '*') '**' pairs.
function findClose(src: string, delim: string, from: number): number {
  let i = from;
  while (i < src.length) {
    if (src[i] === '`') {
      const e = src.indexOf('`', i + 1);
      if (e === -1) return -1;
      i = e + 1;
      continue;
    }
    if (src.startsWith(delim, i)) {
      if (delim === '*' && src.startsWith('**', i)) {
        // a nested bold inside italic — skip over it
        const e = src.indexOf('**', i + 2);
        if (e === -1) return -1;
        i = e + 2;
        continue;
      }
      return i;
    }
    i++;
  }
  return -1;
}

// Matches "[label](href)" starting at src[start] === '['. Label may contain
// balanced brackets; href may contain balanced parentheses.
function matchLink(src: string, start: number): { label: string; href: string; end: number } | null {
  let depth = 0;
  let i = start;
  for (; i < src.length; i++) {
    if (src[i] === '\\') {
      i++;
      continue;
    }
    if (src[i] === '[') depth++;
    else if (src[i] === ']') {
      depth--;
      if (depth === 0) break;
    }
  }
  if (i >= src.length || src[i + 1] !== '(') return null;
  const label = src.slice(start + 1, i);
  let j = i + 2;
  let pd = 1;
  for (; j < src.length; j++) {
    if (src[j] === '(') pd++;
    else if (src[j] === ')') {
      pd--;
      if (pd === 0) break;
    }
  }
  if (j >= src.length) return null;
  const href = src.slice(i + 2, j).trim().split(/\s+/)[0] ?? '';
  return { label, href, end: j + 1 };
}

export function inlineText(nodes: Inline[]): string {
  return nodes
    .map((n) => {
      switch (n.t) {
        case 'text':
        case 'code':
          return n.text;
        case 'image':
          return n.alt;
        default:
          return inlineText(n.children);
      }
    })
    .join('');
}

const RE_HEADING = /^(#{1,3})\s+(.*?)\s*#*\s*$/;
const RE_UL = /^(\s*)[-*+]\s+(.*)$/;
const RE_OL = /^(\s*)\d+[.)]\s+(.*)$/;
const RE_HR = /^\s*([-*_])(\s*\1){2,}\s*$/;
const RE_TABLE_SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const RE_FENCE = /^\s*```/;

function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1);
  const cells: string[] = [];
  let cur = '';
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '\\' && s[i + 1] === '|') {
      cur += '|';
      i++;
    } else if (s[i] === '|') {
      cells.push(cur.trim());
      cur = '';
    } else cur += s[i];
  }
  cells.push(cur.trim());
  return cells;
}

function isBlockStart(line: string, next: string | undefined): boolean {
  return (
    RE_HEADING.test(line) ||
    RE_UL.test(line) ||
    RE_OL.test(line) ||
    RE_HR.test(line) ||
    RE_FENCE.test(line) ||
    /^\s*>/.test(line) ||
    (line.includes('|') && next !== undefined && RE_TABLE_SEP.test(next))
  );
}

export function parseBlocks(lines: string[]): Block[] {
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    if (RE_FENCE.test(line)) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !RE_FENCE.test(lines[i])) body.push(lines[i++]);
      i++;
      blocks.push({ t: 'code', text: body.join('\n') });
      continue;
    }
    const h = RE_HEADING.exec(line);
    if (h) {
      const children = parseInline(h[2]);
      blocks.push({ t: 'heading', level: h[1].length as 1 | 2 | 3, children, text: inlineText(children) });
      i++;
      continue;
    }
    if (RE_HR.test(line) && !RE_UL.test(line)) {
      blocks.push({ t: 'hr' });
      i++;
      continue;
    }
    if (line.includes('|') && i + 1 < lines.length && RE_TABLE_SEP.test(lines[i + 1])) {
      const head = splitRow(line).map(parseInline);
      const align = splitRow(lines[i + 1]).map((c) =>
        c.startsWith(':') && c.endsWith(':') ? 'center' : c.endsWith(':') ? 'right' : c.startsWith(':') ? 'left' : null,
      ) as Array<'left' | 'right' | 'center' | null>;
      i += 2;
      const rows: Inline[][][] = [];
      while (i < lines.length && lines[i].trim() && lines[i].includes('|')) {
        rows.push(splitRow(lines[i]).map(parseInline));
        i++;
      }
      blocks.push({ t: 'table', head, rows, align });
      continue;
    }
    if (/^\s*>/.test(line)) {
      const inner: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) {
        inner.push(lines[i].replace(/^\s*>\s?/, ''));
        i++;
      }
      blocks.push(makeQuote(parseBlocks(inner)));
      continue;
    }
    const ul = RE_UL.exec(line);
    const ol = RE_OL.exec(line);
    if (ul || ol) {
      const ordered = !!ol && !ul;
      const re = ordered ? RE_OL : RE_UL;
      const baseIndent = (ul ?? ol)![1].length;
      const items: Block[][] = [];
      while (i < lines.length) {
        const m = re.exec(lines[i]);
        if (!m || m[1].length !== baseIndent || RE_HR.test(lines[i])) break;
        const itemLines: string[] = [m[2]];
        i++;
        // continuation: indented lines (nested lists / wrapped text) and lazy lines
        while (i < lines.length) {
          const l = lines[i];
          if (!l.trim()) {
            // blank line: continue the item only if next non-blank is indented deeper
            let k = i + 1;
            while (k < lines.length && !lines[k].trim()) k++;
            if (k < lines.length && /^\s+\S/.test(lines[k]) && indentOf(lines[k]) > baseIndent) {
              itemLines.push('');
              i++;
              continue;
            }
            break;
          }
          if (indentOf(l) > baseIndent) {
            itemLines.push(l.slice(Math.min(indentOf(l), baseIndent + 2)));
            i++;
            continue;
          }
          if (isBlockStart(l, lines[i + 1])) break;
          itemLines.push(l.trim()); // lazy continuation
          i++;
        }
        items.push(parseBlocks(itemLines));
        // skip a single blank line between items of the same list
        let k = i;
        while (k < lines.length && !lines[k].trim()) k++;
        if (k < lines.length && k > i) {
          const m2 = re.exec(lines[k]);
          if (m2 && m2[1].length === baseIndent) i = k;
        }
      }
      blocks.push({ t: 'list', ordered, items });
      continue;
    }
    // paragraph
    const para: string[] = [line.trim()];
    i++;
    while (i < lines.length && lines[i].trim() && !isBlockStart(lines[i], lines[i + 1])) {
      para.push(lines[i].trim());
      i++;
    }
    blocks.push({ t: 'paragraph', children: parseInline(para.join(' ')) });
  }
  return blocks;
}

function indentOf(l: string): number {
  return l.length - l.trimStart().length;
}

function makeQuote(children: Block[]): Block {
  const first = children[0];
  if (first && first.t === 'paragraph' && first.children[0]?.t === 'bold') {
    const label = inlineText(first.children[0].children).replace(/\s*:\s*$/, '').trim();
    const rawLabel = inlineText(first.children[0].children).trim();
    const kind = CALLOUT_LABELS[label];
    if (kind && (rawLabel.endsWith(':') || first.children[1]?.t === 'text')) {
      return { t: 'callout', kind, label, children };
    }
  }
  return { t: 'quote', children };
}

export function parseMarkdown(src: string): ParsedDoc {
  const lines = src.replace(/^﻿/, '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = parseBlocks(lines);
  let title: string | null = null;
  const sections: ParsedDoc['sections'] = [];
  for (const b of blocks) {
    if (b.t !== 'heading') continue;
    if (b.level === 1 && title === null) title = b.text;
    else if (b.level === 2) sections.push({ index: sections.length, text: b.text });
  }
  return { blocks, title, sections };
}
